/*
 * BPP — header mega panel + mobile drawer accordion.
 * No dependencies. Loaded `defer` by sections/bpp-header.liquid. Styles live in
 * bpp-nav-mega.css.
 *
 * Two custom elements instead of a section-level script so the theme editor
 * needs no special handling: when it re-renders the header the old elements
 * disconnect (listeners are removed) and the new ones connect (listeners are
 * added). This file can be evaluated any number of times; each element is only
 * defined once.
 *
 * <bpp-nav-mega>      desktop. Triggers [data-bpp-mega-trigger] open the panel
 *                     that their aria-controls names ([data-bpp-mega-panel]).
 * <bpp-nav-accordion> mobile drawer. Triggers [data-bpp-acc-trigger] open the
 *                     group [data-bpp-acc-panel] named by aria-controls.
 */
(() => {
  const HOVER_MS = 150;
  const FOCUSABLE = 'a[href], button:not([disabled])';
  const desktop = window.matchMedia('(min-width: 990px)');

  class BppNavMega extends HTMLElement {
    connectedCallback() {
      this.items = [];
      this.active = null;
      this.pinned = false;
      this.timer = 0;

      this.querySelectorAll('[data-bpp-mega-trigger]').forEach((trigger) => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel || !this.contains(panel)) return;
        // A parent with a real address is an <a> so it still works without this
        // script. Once the script runs, a click opens the panel instead, so it
        // is announced as the button it now is.
        if (trigger.tagName === 'A') trigger.setAttribute('role', 'button');
        this.items.push({ trigger, panel });
      });

      this.onOver = this.onOver.bind(this);
      this.onOut = this.onOut.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onDocClick = (e) => { if (this.active && !this.contains(e.target)) this.close(); };
      this.onDocKey = (e) => { if (e.key === 'Escape' && this.active) this.escape(); };
      this.onViewport = () => { if (!desktop.matches) this.close(); };

      this.addEventListener('pointerover', this.onOver);
      this.addEventListener('pointerout', this.onOut);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onDocKey);
      desktop.addEventListener('change', this.onViewport);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKey);
      desktop.removeEventListener('change', this.onViewport);
    }

    itemFor(node) {
      return this.items.find((i) => i.trigger.contains(node) || i.panel.contains(node));
    }

    open(item, pinned) {
      clearTimeout(this.timer);
      if (this.active && this.active !== item) this.shut(this.active);
      this.active = item;
      this.pinned = pinned;
      item.trigger.setAttribute('aria-expanded', 'true');
      item.panel.setAttribute('data-open', '');
    }

    shut(item) {
      item.trigger.setAttribute('aria-expanded', 'false');
      item.panel.removeAttribute('data-open');
    }

    close() {
      clearTimeout(this.timer);
      if (this.active) this.shut(this.active);
      this.active = null;
      this.pinned = false;
    }

    escape() {
      const { trigger, panel } = this.active;
      const wasInside = panel.contains(document.activeElement);
      this.close();
      if (wasInside) trigger.focus();
    }

    // Hover is for a mouse only. A finger also fires pointerover before its
    // click; reacting to it would open the panel and the click that follows
    // would close it again, and on a desktop-width touch screen (tablet,
    // landscape phone) the panel would flicker or never stay open.
    onOver(e) {
      if (e.pointerType !== 'mouse') return;
      const item = this.itemFor(e.target);
      clearTimeout(this.timer);
      if (item) {
        if (this.active === item) return;
        this.timer = setTimeout(() => this.open(item, false), HOVER_MS);
      } else if (this.active && !this.pinned) {
        // Over a plain top-level link: the open panel no longer belongs.
        this.timer = setTimeout(() => this.close(), HOVER_MS);
      }
    }

    onOut(e) {
      if (e.pointerType !== 'mouse' || this.pinned) return;
      if (e.relatedTarget && this.contains(e.relatedTarget)) return;
      clearTimeout(this.timer);
      if (this.active) this.timer = setTimeout(() => this.close(), HOVER_MS);
    }

    onClick(e) {
      const trigger = e.target.closest('[data-bpp-mega-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      const item = this.items.find((i) => i.trigger === trigger);
      if (!item) return;
      e.preventDefault();
      if (this.active !== item) {
        this.open(item, true);
      } else if (!this.pinned && e.detail > 0) {
        // Opened by hovering, now clicked with the mouse: the shopper means
        // "keep it open", not "close what I just opened".
        this.pinned = true;
      } else {
        this.close();
      }
    }

    onKey(e) {
      const item = this.itemFor(e.target);
      if (!item) return;
      const { trigger, panel } = item;
      const onTrigger = trigger.contains(e.target);
      const links = [...panel.querySelectorAll(FOCUSABLE)];

      if (onTrigger) {
        if (e.key === ' ' && trigger.tagName === 'A') {
          e.preventDefault();
          trigger.click();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.open(item, true);
          links[0]?.focus();
        } else if (e.key === 'Tab' && !e.shiftKey && this.active === item && links.length) {
          // The panel sits after the whole rail in the DOM, so a plain Tab
          // would skip over it to the next top-level item.
          e.preventDefault();
          links[0].focus();
        }
        return;
      }

      const i = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        links[Math.min(i + 1, links.length - 1)]?.focus();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        (i > 0 ? links[i - 1] : trigger).focus();
      } else if (e.key === 'Tab' && e.shiftKey && i === 0) {
        e.preventDefault();
        trigger.focus();
      } else if (e.key === 'Tab' && !e.shiftKey && i === links.length - 1) {
        // Leaving the panel forwards: carry on from the trigger's neighbour.
        const next = [...this.querySelectorAll('.bpp-header__catlist a[href], .bpp-header__catlist button')];
        const after = next[next.indexOf(trigger) + 1];
        this.close();
        if (after) {
          e.preventDefault();
          after.focus();
        }
      }
    }

    // Focus moving anywhere that is not the open item's own trigger or panel
    // closes it, including to a neighbouring top-level link that is still inside
    // this element. A focusout with no relatedTarget is a click on a
    // non-focusable surface or a window blur; the document click handler deals
    // with the former.
    onFocusOut(e) {
      const to = e.relatedTarget;
      const a = this.active;
      if (a && to && !a.trigger.contains(to) && !a.panel.contains(to)) this.close();
    }
  }

  class BppNavAccordion extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      // Capture phase, on purpose. MenuDrawer (global.js) attached closeSubmenu()
      // to every <button> in the drawer when it was constructed; letting this
      // click reach it would close the panel without lifting the scroll lock.
      this.addEventListener('click', this.onClick, true);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick, true);
    }

    onClick(e) {
      const trigger = e.target.closest('[data-bpp-acc-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      e.stopPropagation();
      e.preventDefault();
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      const open = !panel.hasAttribute('data-open');
      panel.toggleAttribute('data-open', open);
      trigger.setAttribute('aria-expanded', String(open));
    }
  }

  if (!customElements.get('bpp-nav-mega')) customElements.define('bpp-nav-mega', BppNavMega);
  if (!customElements.get('bpp-nav-accordion')) customElements.define('bpp-nav-accordion', BppNavAccordion);
})();
